import { money, moneyK } from './format.js';

const SOURCES = [
  { key: 'employment', label: 'Employment', color: '#1a2040' },
  { key: 'socialSecurity', label: 'Social Security', color: '#015697' },
  { key: 'pension', label: 'Pension', color: '#1a7a4a' },
  { key: 'other', label: 'Other income', color: '#8b95a3' },
];

// Show a readable window rather than every year to 95: the story is the next
// couple of decades, and 35 hairline columns print as noise.
const WINDOW_YEARS = 22;

// `horizon` says where the timeline ends and for whom, because "beyond 95"
// means nothing until it says whose 95. The timeline runs to the younger
// person's, so the figure covers both lives.
export default function IncomeGapSection({ timeline, summary, horizon = null }) {
  if (!timeline || !timeline.length) return null;

  const rows = timeline.slice(0, WINDOW_YEARS);
  const ceiling = Math.max(
    ...rows.map(r => Math.max(r.totalIncome, r.spendingNeed))
  ) || 1;

  const first = rows[0];
  const gapNow = summary.gapNow;
  const covered = summary.coverage;

  return (
    <section className="asmt-card" id="income">
      <div className="asmt-card-head">
        <h2>What comes in, against what you spend</h2>
        <span className="asmt-card-note">Today through {rows[rows.length - 1].year}</span>
      </div>

      <p className="asmt-lede">
        The bars are income you can count on: Social Security, pensions and any
        salary. The dashed line is what you told us it costs to live. Whatever
        sits above the bars has to come out of your savings.
      </p>

      <div className="asmt-timeline">
        {rows.map(row => {
          const needPct = (row.spendingNeed / ceiling) * 100;
          return (
            <div className="asmt-tl-col" key={row.year} title={`${row.year}: ${money(row.totalIncome)} income against ${money(row.spendingNeed)} spending`}>
              <div className="asmt-tl-stack">
                {SOURCES.map(src => {
                  const v = row.bySource[src.key] || 0;
                  if (!v) return null;
                  return (
                    <div
                      key={src.key}
                      className={`asmt-tl-seg is-${src.key}`}
                      style={{ height: `${(v / ceiling) * 190}px`, background: src.color }}
                    />
                  );
                })}
              </div>
              <div className="asmt-tl-need" style={{ bottom: `${needPct}%` }} />
            </div>
          );
        })}
      </div>

      <div className="asmt-tl-axis">
        {rows.map((row, i) => (
          <div key={row.year}>{i === 0 || row.year % 5 === 0 ? row.year : ''}</div>
        ))}
      </div>

      <div className="asmt-legend">
        {SOURCES.map(src => (
          <span key={src.key}>
            <span className="asmt-legend-key" style={{ background: src.color }} />
            {src.label}
          </span>
        ))}
        <span>
          <span className="asmt-legend-key" style={{ background: 'transparent', borderTop: '2px dashed #c0392b', height: 0, marginBottom: 4 }} />
          What you spend
        </span>
      </div>

      <div className="asmt-tiles">
        <div className="asmt-tile">
          <div className="asmt-tile-label">Gap this year</div>
          <div className={`asmt-tile-value ${gapNow > 0 ? 'is-gap' : 'is-good'}`}>
            {gapNow > 0 ? money(gapNow) : money(Math.abs(gapNow))}
          </div>
          <div className="asmt-tile-note">
            {gapNow > 0
              ? `${money(first.spendingNeed)} spending against ${money(first.totalIncome)} income`
              : 'Income covers spending today'}
          </div>
        </div>

        {summary.gapAfterStepDown !== null && (
          <div className="asmt-tile">
            <div className="asmt-tile-label">Once spending changes</div>
            <div className={`asmt-tile-value ${summary.gapAfterStepDown > 0 ? 'is-gap' : 'is-good'}`}>
              {money(Math.abs(summary.gapAfterStepDown))}
            </div>
            <div className="asmt-tile-note">
              {summary.gapAfterStepDown > 0 ? 'still drawn from savings' : 'covered by income'}
            </div>
          </div>
        )}

        <div className="asmt-tile">
          <div className="asmt-tile-label">Largest annual gap</div>
          <div className="asmt-tile-value">{moneyK(summary.peakGap)}</div>
          <div className="asmt-tile-note">across the years shown</div>
        </div>

        <div className="asmt-tile">
          <div className="asmt-tile-label">Savings cover the gap</div>
          <div className={`asmt-tile-value ${covered.neverDepletes ? 'is-good' : ''}`}>
            {covered.neverDepletes ? 'Beyond 95' : `${covered.yearsCovered} yrs`}
          </div>
          <div className="asmt-tile-note">
            {covered.neverDepletes
              ? (horizon && horizon.name && horizon.other
                ? `through ${horizon.year}, when ${horizon.name} is ${horizon.age} and ${horizon.other.name} would be ${horizon.other.age}, before any growth is assumed`
                : 'before any growth is assumed')
              : covered.depletionYear && covered.depletionName
                ? `to about ${covered.depletionYear}, when ${covered.depletionName} is ${covered.depletionAge}, before any growth is assumed`
                : `to about age ${covered.depletionAge ?? '-'}, before any growth is assumed`}
          </div>
        </div>
      </div>

      {/* Saying this out loud is what keeps the number defensible in the room:
          it is deliberately the conservative version. */}
      <div className="asmt-callout is-info">
        These figures assume your savings earn nothing at all. That is not a
        forecast, it is a floor: it shows what the plan survives before any
        investment return is counted, so nothing here depends on a market
        assumption we would have to defend.
      </div>
    </section>
  );
}
